/**
 * EntityFormFields component.
 *
 * Auto-renders all visible fields from the form configuration, organised by
 * sections when configured. This is the convenience alternative to manually
 * placing each `<EntityForm.Field>` -- simply drop `<EntityForm.Fields />`
 * and the component will:
 *
 * 1. Collect all field entries from `config.fields`.
 * 2. Exclude any field names specified in the `exclude` prop.
 * 3. Filter out fields that are not currently visible.
 * 4. Sort the remaining fields by their `order` property (ascending).
 * 5. Group fields by section (when `config.sections` is defined and non-empty).
 * 6. Render each group inside an `EntityFormSection` with a `Grid` layout,
 *    or render a flat `Grid` when no sections are configured.
 *
 * @see Spec section 3.6 for the EntityFormFields contract.
 * @see types.ts `EntityFormFieldsProps` for prop shape details.
 */

import * as React from 'react';
import { Grid } from '@components/base/layout/Grid/Grid';
import { useEntityFormContext } from '../context/EntityFormContext';
import { EntityFormField } from './EntityFormField';
import { EntityFormSection } from './EntityFormSection';
import { COLUMN_CLASS_MAP, DEFAULT_LAYOUT } from '../constants';
import type { EntityFormFieldsProps, FieldConfig } from '../types';
import type { GridGap } from '@components/base/layout/Grid/Grid';

// ---------------------------------------------------------------------------
// Gap Token Mapping
// ---------------------------------------------------------------------------

/**
 * Maps the EntityForm gap size tokens (`'sm' | 'md' | 'lg'`) to the
 * numeric `GridGap` values accepted by the `Grid` component.
 *
 * - `sm` -> 3 (gap-3 = 12px)
 * - `md` -> 5 (gap-5 = 20px)
 * - `lg` -> 8 (gap-8 = 32px)
 */
const GAP_TO_GRID_GAP: Record<string, GridGap> = {
  sm: 4,
  md: 5,
  lg: 8,
};

// ---------------------------------------------------------------------------
// Sorting Helper
// ---------------------------------------------------------------------------

/**
 * Comparator that sorts `[name, FieldConfig]` entries by the field's
 * `order` property in ascending order. Fields without an `order` value
 * are pushed to the end.
 */
function sortByOrder(
  a: [string, FieldConfig],
  b: [string, FieldConfig],
): number {
  const aOrder = a[1].order ?? Number.MAX_SAFE_INTEGER;
  const bOrder = b[1].order ?? Number.MAX_SAFE_INTEGER;
  return aOrder - bOrder;
}

// ---------------------------------------------------------------------------
// EntityFormFields Component
// ---------------------------------------------------------------------------

/**
 * Auto-render compound component for EntityForm.
 *
 * Renders all visible fields from the form configuration in a `Grid`
 * layout, optionally grouped by sections. Fields can be excluded via
 * the `exclude` prop for cases where some fields need custom placement.
 *
 * Must be rendered inside an `<EntityForm>` provider.
 *
 * @example
 * ```tsx
 * // Render all fields automatically
 * <EntityForm config={config}>
 *   <EntityForm.Fields />
 * </EntityForm>
 * ```
 *
 * @example
 * ```tsx
 * // Exclude specific fields for custom placement
 * <EntityForm config={config}>
 *   <EntityForm.Fields exclude={['avatar', 'notes']} />
 *   <CustomAvatarUploader />
 *   <EntityForm.Field name="notes" />
 * </EntityForm>
 * ```
 */
export function EntityFormFields({
  exclude,
  className,
}: Readonly<EntityFormFieldsProps>): React.ReactElement {
  const { config, form } = useEntityFormContext();

  // --- Resolve layout values ---

  const layout = { ...DEFAULT_LAYOUT, ...config.layout };
  const columns = COLUMN_CLASS_MAP[layout.columns];
  const gridGap = GAP_TO_GRID_GAP[layout.gap];

  // --- Collect and filter fields ---

  const excludeSet = exclude ? new Set(exclude) : undefined;

  const visibleFields = Object.entries(config.fields)
    .filter(([name]) => {
      if (excludeSet?.has(name)) {
        return false;
      }
      return form.isFieldVisible(name);
    })
    .sort(sortByOrder);

  // --- Build container class names ---

  const sectionContainerClasses =
    ['space-y-6', config.cssOverrides?.fields, className].filter(Boolean).join(' ');
  const flatContainerClasses =
    [config.cssOverrides?.fields, className].filter(Boolean).join(' ') || undefined;

  // --- Section grouping ---

  const hasSections =
    config.sections !== undefined && config.sections.length > 0;

  if (hasSections) {
    // Group fields by their section property
    const sectionGroups = new Map<string | undefined, [string, FieldConfig][]>();
    for (const entry of visibleFields) {
      const sectionId = entry[1].section;
      const group = sectionGroups.get(sectionId) ?? [];
      group.push(entry);
      sectionGroups.set(sectionId, group);
    }

    // Sort sections by order
    const sortedSections = [...(config.sections ?? [])].sort(
      (a, b) =>
        (a.order ?? Number.MAX_SAFE_INTEGER) -
        (b.order ?? Number.MAX_SAFE_INTEGER),
    );

    // Fields without a section go at the end
    const defaultGroup = sectionGroups.get(undefined);

    return (
      <div className={sectionContainerClasses} data-testid="entity-form-fields">
        {sortedSections.map((section) => {
          const sectionFields = sectionGroups.get(section.id);
          if (!sectionFields || sectionFields.length === 0) {
            return null;
          }
          return (
            <EntityFormSection key={section.id} id={section.id}>
              <Grid columns={columns} gap={gridGap}>
                {sectionFields.map(([fieldName]) => (
                  <EntityFormField key={fieldName} name={fieldName} />
                ))}
              </Grid>
            </EntityFormSection>
          );
        })}
        {defaultGroup && defaultGroup.length > 0 && (
          <Grid columns={columns} gap={gridGap}>
            {defaultGroup.map(([fieldName]) => (
              <EntityFormField key={fieldName} name={fieldName} />
            ))}
          </Grid>
        )}
      </div>
    );
  }

  // --- Flat layout (no sections) ---

  return (
    <Grid
      columns={columns}
      gap={gridGap}
      className={flatContainerClasses}
      data-testid="entity-form-fields"
    >
      {visibleFields.map(([fieldName]) => (
        <EntityFormField key={fieldName} name={fieldName} />
      ))}
    </Grid>
  );
}

EntityFormFields.displayName = 'EntityFormFields';
